import { JSX, createSignal, createEffect } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";

export interface ProfileModalProps {
  onSubmit: (name: string, email: string) => Promise<void>;
  onClose: () => void;
}

export function ProfileModal(props: ProfileModalProps): JSX.Element {
  const { components } = useTelescopeContext();

  const [name, setName] = createSignal<string>("");
  const [email, setEmail] = createSignal<string>("");
  const [isLoading, setIsLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<string | undefined>(undefined);
  const [nameError, setNameError] = createSignal<string | undefined>(undefined);
  const [emailError, setEmailError] = createSignal<string | undefined>(
    undefined
  );

  createEffect(() => {
    if (name()) setNameError(undefined);
  });

  createEffect(() => {
    if (email()) setEmailError(undefined);
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e?: Event) => {
    e?.preventDefault();

    // Validate
    if (!name().trim()) {
      setNameError("Please enter your name");
      return;
    }

    if (!email().trim()) {
      setEmailError("Please enter your email");
      return;
    }

    if (!validateEmail(email().trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      await props.onSubmit(name().trim(), email().trim());
    } catch (err: any) {
      setError(err?.message ?? "Failed to update profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div class="flex w-full flex-col ">
      <components.ModalHeader
        size="large"
        title="Welcome to Hubble"
        description="Lets setup your account"
        onClose={props.onClose}
      />

      <form onSubmit={handleSubmit} class="flex flex-col">
        <div class="flex flex-col gap-4 p-5">
          <components.Input
            value={name()}
            onChange={(value: string) => setName(value)}
            placeholder="Enter your name"
            size={40}
            type="text"
            label="Name"
            required={true}
            state={nameError() ? "error" : "default"}
            helperText={nameError()}
          />

          <components.Input
            value={email()}
            onChange={(value: string) => setEmail(value)}
            placeholder="Enter your email"
            size={40}
            type="text"
            label="Email"
            required={true}
            state={emailError() ? "error" : "default"}
            helperText={emailError()}
          />

          {error() && (
            <p class="text-center text-label-regular text-error-base">
              {error()}
            </p>
          )}
        </div>

        <components.ModalFooter
          button={{
            text: "Continue",
            onClick: handleSubmit,
            variant: "primary",
            style: "neutral",
            size: 40,
            loading: isLoading(),
            disabled: isLoading(),
          }}
        />
      </form>
    </div>
  );
}
